/**
 * Canonical nested-value accessor (9o.6.e Commit 8.5).
 *
 * Single source of truth for dot-path lookups on the canonical player shape
 * (and any other nested object). Replaces 5 substantively-identical
 * implementations that previously lived in:
 *   - services/tables/DynastyTable.js
 *   - components/mobile/components/MobileDynastyTable.js
 *   - pages/league-dashboard/free-agent-search/VirtualPlayerTable.js
 *   - pages/league-dashboard/live-draft/utils/draftHelpers.js
 *   - pages/league-dashboard/live-draft/components/VirtualAvailablePlayersTable.js
 *
 * SCHEMA-AWARE STRICT MODE
 * ------------------------
 * The previous implementations walked dot-paths blindly with `current?.[key]`,
 * returning undefined on any traversal failure. That meant a typo like
 * `stats.last_7_days.hitting.batting_avg` (wrong key — should be `rolling_7_day`)
 * silently rendered a blank cell instead of surfacing the bug.
 *
 * Strict mode (default in dev + test, off in production) distinguishes:
 *
 *   - Schema-allowed null intermediate (e.g., `player.projected_contract = null`):
 *     traversal stops at null, returns undefined silently.
 *
 *   - Schema-allowed empty object intermediate (e.g., `player.league_context.team = {}`
 *     for free agents per Bible § 4.5): leaf access on `{}` returns undefined
 *     silently — empty object signals "this canonical block doesn't apply here."
 *
 *   - Typo at intermediate key (e.g., `stats.last_7_days.hitting.batting_avg`):
 *     key absent → logs error with available keys at that depth.
 *
 *   - Typo at leaf when parent has other keys (e.g., `stats.season.hitting.bating_avg`
 *     when parent has batting_avg + home_runs etc.): logs error with sibling list.
 *
 *   - Non-object intermediate traversal (trying to descend into a number/string):
 *     logs error.
 *
 * Production builds skip strict checks entirely — zero runtime cost, identical
 * behavior to the pre-Commit-8.5 helpers.
 *
 * Callers that legitimately need silent behavior (e.g., totally-untrusted data
 * sources, incomplete test mocks) can pass `{ strict: false }`.
 */

const IS_PROD = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';

/**
 * Walk a dot-path on an object, returning the value or undefined.
 *
 * @param {*}      obj                 - Source object.
 * @param {string} path                - Dot-separated path, e.g. 'stats.season.hitting.batting_avg'.
 * @param {Object} [options]
 * @param {boolean} [options.strict]   - Override the env-derived default. true → log typos; false → silent.
 * @returns {*} Value at path, or undefined if any segment is unreachable.
 */
export const getNestedValue = (obj, path, options = undefined) => {
  if (!obj || !path) return undefined;

  // Legacy escape hatch: some callers pass literal dotted keys. If the source
  // object has the entire path as an own property (e.g. a flat row), return it
  // directly. Matches the prior DynastyTable.js behavior verbatim.
  if (typeof obj === 'object' && Object.prototype.hasOwnProperty.call(obj, path)) {
    return obj[path];
  }

  if (!path.includes('.')) {
    return obj[path];
  }

  const strict = options && typeof options.strict === 'boolean' ? options.strict : !IS_PROD;
  const parts = path.split('.');
  let current = obj;

  for (let i = 0; i < parts.length; i++) {
    const key = parts[i];
    const isLeaf = i === parts.length - 1;
    const traversedPath = parts.slice(0, i).join('.') || '(root)';

    // Schema-allowed: traversal hit a null/undefined intermediate.
    // (Backend may emit `null` for optional canonical blocks like projected_contract.)
    if (current == null) {
      return undefined;
    }

    // Non-object intermediate is always a typo: caller asked us to traverse
    // a primitive. Even an empty-object child wouldn't allow this.
    if (typeof current !== 'object') {
      if (strict) {
        // eslint-disable-next-line no-console
        console.error(
          `[getNestedValue] Path '${path}' tried to traverse into a ` +
            `${typeof current} at '${traversedPath}'. Likely typo or misuse.`
        );
      }
      return undefined;
    }

    if (!(key in current)) {
      // Key absent at this depth.
      if (isLeaf) {
        // Leaf miss is informational, not always a typo. Bible § 4.5 emits
        // empty objects for canonical blocks that don't apply (e.g., a FA's
        // league_context.team is `{}`, so 'team_name' legitimately absent).
        // Only flag when the parent has OTHER keys — that's the "this leaf
        // doesn't exist but its siblings do" signal.
        const siblingKeys = Object.keys(current);
        if (strict && siblingKeys.length > 0) {
          // eslint-disable-next-line no-console
          console.error(
            `[getNestedValue] Path '${path}' references leaf '${key}' which is ` +
              `absent. Parent at '${traversedPath}' has keys: [${siblingKeys.join(', ')}]. ` +
              `Likely typo on the leaf field name.`
          );
        }
        return undefined;
      }
      // Intermediate miss: distinguish typo (parent has sibling keys) from
      // canonical empty-block (parent is `{}` per Bible § 4.5 — backend emits
      // empty objects via `COALESCE(..., '{}'::jsonb)` for slots that don't
      // apply, e.g., `stats.season = {}` for a player with no season data).
      // Mirrors the leaf-handler structure at lines 100-115.
      const availableKeys = Object.keys(current);
      if (strict && availableKeys.length > 0) {
        // eslint-disable-next-line no-console
        console.error(
          `[getNestedValue] Path '${path}' references intermediate '${key}' ` +
            `(depth ${i}) which is absent. Parent at '${traversedPath}' has keys: ` +
            `[${availableKeys.join(', ')}]. Likely typo.`
        );
      }
      return undefined;
    }

    current = current[key];
  }

  return current;
};

export default getNestedValue;
